import { useState } from "react";
import { Calendar as CalendarIcon, ChevronDown, Clock, Minus, Plus } from "lucide-react";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { LOCALES, useLang } from "@/lib/i18n";

export const inputCls = "h-14 w-full rounded-2xl border border-ink/10 bg-white px-4 text-[15px] font-medium text-ink outline-none transition-[border-color,box-shadow] placeholder:font-normal placeholder:text-slate/55 focus:border-miles focus:ring-4 focus:ring-miles/10";

const dateFromISO = (value) => value ? new Date(`${value}T12:00:00`) : undefined;
const dateToISO = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

export const FieldLabel = ({ icon: Icon, children }) => (
  <span className="mb-2 flex items-center gap-2 text-sm font-medium text-slate">
    {Icon && <Icon className="h-4 w-4 text-miles" strokeWidth={1.5} />}
    {children}
  </span>
);

export const DateField = ({ label, value, onChange, min, testId }) => {
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const selected = dateFromISO(value);
  const minDate = min ? dateFromISO(min) : undefined;
  const display = selected
    ? new Intl.DateTimeFormat(LOCALES[lang], { weekday: "short", day: "numeric", month: "short" }).format(selected)
    : label;

  return (
    <div>
      <FieldLabel icon={CalendarIcon}>{label}</FieldLabel>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" data-testid={testId} className={`${inputCls} flex items-center justify-between gap-3 text-left`}>
            <span className={selected ? "text-ink" : "text-slate/55"}>{display}</span>
            <ChevronDown className="h-4 w-4 shrink-0 text-slate" strokeWidth={1.5} />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto rounded-3xl border-ink/10 bg-white p-2 shadow-[0_24px_70px_rgba(16,24,40,0.14)]">
          <CalendarPicker
            mode="single"
            selected={selected}
            onSelect={(date) => {
              if (!date) return;
              onChange(dateToISO(date));
              setOpen(false);
            }}
            disabled={minDate ? { before: minDate } : undefined}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

export const TimeField = ({ label, value, onChange, testId }) => (
  <div>
    <FieldLabel icon={Clock}>{label}</FieldLabel>
    <input
      type="time"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className={`${inputCls} text-left [-webkit-appearance:none] [&::-webkit-date-and-time-value]:text-left [&::-webkit-datetime-edit]:text-left`}
    />
  </div>
);

export const Counter = ({ label, value, min, max, onChange, testId }) => (
  <div className="flex items-center justify-between gap-4 rounded-2xl bg-cloud px-4 py-3">
    <span className="text-sm font-medium text-ink">{label}</span>
    <span className="flex items-center gap-3">
      <button type="button" onClick={() => onChange(Math.max(min, value - 1))} data-testid={`${testId}-minus`}
        className="flex h-9 w-9 items-center justify-center rounded-full border border-ink/10 bg-white transition-colors hover:border-miles hover:text-miles">
        <Minus className="h-4 w-4" strokeWidth={1.5} />
      </button>
      <span className="w-6 text-center font-mono text-sm font-semibold" data-testid={`${testId}-value`}>{value}</span>
      <button type="button" onClick={() => onChange(Math.min(max, value + 1))} data-testid={`${testId}-plus`}
        className="flex h-9 w-9 items-center justify-center rounded-full border border-ink/10 bg-white transition-colors hover:border-miles hover:text-miles">
        <Plus className="h-4 w-4" strokeWidth={1.5} />
      </button>
    </span>
  </div>
);
